import { connect, useDispatch } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    Checkbox,
    Col,
    DatePicker,
    Form,
    Input,
    InputNumber,
    message,
    Popover,
    Row,
    Select,
    TimePicker,
    Typography,
} from "antd";
import { FaMagic } from "react-icons/fa";
import { SketchPicker } from "react-color";
import moment from "moment";
import tzlookup from "tz-lookup"
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { get, pick } from "lodash";
import axios from "axios";
import { URL_API_CREATE_CUSTOM_MOON_PHASE, XKEY_API_CREATE_CUSTOM_MOON_PHASE } from "../../../../config";
import { uniqueID } from "../../Utilities/helper";
import { ARTWORK } from "../../../../types";
import { MinusCircleOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";

const GOOGLE_MAPS_API_KEY = "AIzaSyD2Kty-jKboVyvy-mnWMDd5JUzC4qE5pqc";
const GOOGLE_MAPS_LIBRARIES = ["places"];

const { Option } = Select;
const { Text } = Typography;

const REFERENCE_TYPES = [
    "birthday",
    "wedding_date",
    "anniversary",
    "memorial",
    "baby_birth",
    "date_met",
];

const dateValueProps = (format) => ({
    getValueProps: (value) => ({ value: value ? moment(value, format) : undefined }),
    normalize: (value) => (value ? value.format(format) : null),
});

const flattenImageLayers = (layers = [], excludeId) => {
    let result = [];
    for (const l of layers) {
        if (l.type === "Image" && l.id !== excludeId) result.push(l);
        if (Array.isArray(l.layers)) result = result.concat(flattenImageLayers(l.layers, excludeId));
    }
    return result;
};

const MoonColorPreview = ({ value }) => (
    <div
        style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            border: "1px solid #d9d9d9",
            background: value || "#000000",
        }}
    />
);

const MoonColorSketchPicker = ({ value, onChange }) => {
    const [localColor, setLocalColor] = useState(value || "#000000");
    const latestColorRef = useRef(localColor);

    useEffect(() => {
        const next = value || "#000000";
        setLocalColor(next);
        latestColorRef.current = next;
    }, [value]);

    const commit = () => {
        if (latestColorRef.current !== (value || "#000000")) {
            onChange(latestColorRef.current);
        }
    };

    return (
        <div onMouseUp={commit} onTouchEnd={commit}>
            <SketchPicker
                width={220}
                color={localColor}
                onChange={({ hex }) => {
                    setLocalColor(hex);
                    latestColorRef.current = hex;
                }}
            />
        </div>
    );
};

const MoonPhaseSetting = ({ layer, workspace }) => {
    const [loading, setLoading] = useState(false)
    const dispatch = useDispatch();
    const form = Form.useFormInstance();
    const autoMap = useRef();
    const [editColorKey, setEditColorKey] = useState(null);

    useEffect(() => {
        if (!editColorKey) return undefined;
        const handleDocMouseDown = (e) => {
            if (!e.target.closest(".moon-color-edit-popover-overlay")) {
                setEditColorKey(null);
            }
        };
        document.addEventListener("mousedown", handleDocMouseDown);
        return () => document.removeEventListener("mousedown", handleDocMouseDown);
    }, [editColorKey]);
    const allLayers = get(workspace, `artwork.templates[${get(workspace, "selectedTemplate")}].layers`, []) || [];
    const imageLayers = flattenImageLayers(allLayers, layer.id);
    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const handlePickMoonColor = (color) => {
        dispatch({
            type: ARTWORK.SET_LAYER,
            payload: {
                ...layer,
                configsMoonPhase: {
                    ...layer.configsMoonPhase,
                    render: {
                        ...layer.configsMoonPhase.render,
                        moon_color: color,
                    },
                },
            },
        });
    };

    const OnchangePlacesAutocomplete = () => {
        const AutoMap = autoMap.current;
        const place = AutoMap.state.autocomplete.getPlace();
        const geometry = place && place.geometry && place.geometry.location;
        if (!geometry) return;
        const formatted_address = place.formatted_address || "";
        const timezone = tzlookup(geometry.lat(), geometry.lng());
        dispatch({
            type: ARTWORK.SET_LAYER,
            payload: {
                ...layer,
                formatted_address,
                configsMoonPhase: {
                    ...layer.configsMoonPhase, 
                    timezone,
                    location: {
                        latitude: geometry.lat(),
                        longitude: geometry.lng(),
                    }
                }
            },
        });
    };

    const handleRecreate = async () => {
        setLoading(true)
        const key = "creLayerMoonPhase";
        message.loading({
            content: "Creating image...",
            key,
            duration: 0,
        });
        

        try {
            const req = await axios.post(URL_API_CREATE_CUSTOM_MOON_PHASE, { ...layer.configsMoonPhase }, {
                headers: {
                    'X-ABN-KEY': XKEY_API_CREATE_CUSTOM_MOON_PHASE,
                    'content-type': 'application/json'
                }
            }).then(res => {
                return get(res, 'data.render', false);
            }).catch(() => {
                return false;
            });

            if (!req) {
                message.error({
                    content: "Created new Moon Phase Class failure!",
                    key,
                    duration: 4,
                });
                setLoading(false)
                return;
            }

            const valuesLayer = get(layer, "values", []);
            valuesLayer[0].file = pick({
                id: uniqueID(),
                key: req.png_file.replace(/^\//, '')
            }, ["id", "key"]);
            dispatch({
                type: ARTWORK.SET_LAYER,
                payload: {
                    ...layer,
                    values: valuesLayer,
                    configsMoonPhase: { ...layer.configsMoonPhase }
                },
            });

            message.success({
                content: "Successfully!",
                key,
                duration: 4,
            });
            setLoading(false)
        } catch (error) {
            message.error({
                content: "Failure!!",
                key,
                duration: 4,
            });
            setLoading(false)
        }
        
    }

    return layer.imageMoonPhase ? <>
        <div>
            <Text strong>General</Text>
            <Row gutter={8}>
                <Col span={24} style={{ display: "none" }}>
                    <Form.Item
                        label="Moon color"
                        name={["configsMoonPhase", "render", "moon_color"]}
                        initialValue={layer.configsMoonPhase.render.moon_color}
                    >
                        <MoonColorPreview />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Text strong style={{ display: "block", marginBottom: 6 }}>Moon Colors</Text>
                    <Form.List
                        name="MoonColors"
                        rules={[
                            {
                                validator: async (_, backgroundcolors) => {
                                    if (!backgroundcolors || backgroundcolors.length < 1) {
                                        return Promise.reject(new Error('At least 1 color'));
                                    }
                                },
                            },
                        ]}
                    >
                        {(fields, { add, remove }, { errors }) => (
                            <div>
                                <div className="Group_color_star_sky" style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                                    {fields.map((field, index) => {
                                        const entry = get(layer, `MoonColors[${index}]`, {});
                                        const isActive = !!entry.color && entry.color === get(layer, "configsMoonPhase.render.moon_color");
                                        return (
                                            <div
                                                className="Box_colors_list"
                                                key={field.key}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 8,
                                                    background: "#fafafa",
                                                    border: "1px solid #e8e8e8",
                                                    borderRadius: 8,
                                                    padding: "6px 10px 6px 6px",
                                                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                                }}
                                            >
                                                <Popover
                                                    trigger={[]}
                                                    visible={editColorKey === field.key}
                                                    onVisibleChange={(v) => setEditColorKey(v ? field.key : null)}
                                                    overlayClassName="moon-color-edit-popover-overlay"
                                                    placement="bottom"
                                                    title="Edit color"
                                                    content={
                                                        <Form.Item
                                                            name={[field.name, "color"]}
                                                            validateTrigger={['onChange', 'onBlur']}
                                                            noStyle
                                                        >
                                                            <MoonColorSketchPicker />
                                                        </Form.Item>
                                                    }
                                                >
                                                    <div
                                                        onClick={() => entry.color && handlePickMoonColor(entry.color)}
                                                        onDoubleClick={() => setEditColorKey(field.key)}
                                                        title={entry.color ? "Click to use as the Moon color · double-click to edit color" : "Double-click to pick a color"}
                                                        style={{
                                                            width: 26,
                                                            height: 26,
                                                            borderRadius: 6,
                                                            background: entry.color || "#000000",
                                                            border: isActive ? "2px solid #1890ff" : "1px solid #d9d9d9",
                                                            boxShadow: isActive ? "0 0 0 2px rgba(24,144,255,0.25)" : "none",
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </Popover>
                                                <div style={{ width: 1, alignSelf: "stretch", background: "#e8e8e8" }} />
                                                <Popover
                                                    trigger="click"
                                                    placement="bottom"
                                                    title="Edit color"
                                                    content={
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 12, width: 240 }}>
                                                            <Form.Item
                                                                name={[field.name, "color"]}
                                                                validateTrigger={['onChange', 'onBlur']}
                                                                noStyle
                                                            >
                                                                <Input className="color-picker-moonphase" type="color" style={{ width: 24, height: 24, padding: 0, flexShrink: 0, transform: 'scale(1.5)' }} />
                                                            </Form.Item>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <Form.Item
                                                                    name={[field.name, "layerIds"]}
                                                                    noStyle
                                                                >
                                                                    <Select
                                                                        mode="multiple"
                                                                        allowClear
                                                                        showSearch
                                                                        optionFilterProp="children"
                                                                        style={{ width: "100%" }}
                                                                        placeholder="Show these image layers"
                                                                    >
                                                                        {imageLayers.map((l) => (
                                                                            <Option key={l.id} value={l.id}>{l.title || l.id}</Option>
                                                                        ))}
                                                                    </Select>
                                                                </Form.Item>
                                                            </div>
                                                        </div>
                                                    }
                                                >
                                                    <SettingOutlined
                                                        title="Edit color / linked layer"
                                                        style={{ fontSize: 15, color: "rgba(0,0,0,0.45)", cursor: "pointer" }}
                                                    />
                                                </Popover>
                                                {fields.length > 1 ? (
                                                    <>
                                                        <div style={{ width: 1, alignSelf: "stretch", background: "#e8e8e8" }} />
                                                        <MinusCircleOutlined
                                                            title="Remove color"
                                                            onClick={() => remove(field.name)}
                                                            className="moon-remove-color-icon"
                                                            style={{ fontSize: 15, cursor: "pointer" }}
                                                        />
                                                    </>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                                <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
                                    <>
                                        <Button
                                            type="dashed"
                                            onClick={() => add({ color: "#000001", layerIds: [] })}
                                            style={{ width: '50%', display: "block" }}
                                            icon={<PlusOutlined />}
                                            className="Button_add_color"
                                        >
                                            Add color
                                        </Button>
                                        <Form.ErrorList errors={errors} />
                                    </>
                                </Form.Item>

                            </div>
                        )}
                    </Form.List>
                </Col>
                
                <Col span={12}>
                    <Form.Item
                        label="Date"
                        name={["configsMoonPhase", "date"]}
                        rules={[{ required: true, message: "Date is required" }]}
                        {...dateValueProps("YYYY-MM-DD")}
                    >
                        <DatePicker
                            style={{ width: "100%" }}
                            format={"YYYY-MM-DD"}
                        />
                    </Form.Item>
                </Col>
                
                <Col span={12}>
                    <Form.Item
                        label="Time"
                        name={["configsMoonPhase", "time"]}
                        {...dateValueProps("HH:mm:ss")}
                    >
                        <TimePicker
                            style={{ width: "100%" }}
                            format={"HH:mm:ss"}
                            allowClear
                        />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item
                        name={["isDisplaySelectTime"]}
                        valuePropName="checked"
                        initialValue={false}
                    >
                        <Checkbox>Display select time</Checkbox>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={8}>
                <Col span={12} style={{display: 'none'}}>
                    <Form.Item
                        label="Timezone"
                        name={["configsMoonPhase", "timezone"]}
                        extra="IANA timezone name. Leave empty for UTC."
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12} style={{
                    display: 'none'
                }}>
                    <Form.Item
                        label="Reference type"
                        name={["configsMoonPhase", "reference_type"]}
                        extra="Metadata only, does not affect the image."
                        normalize={(value) => (value || "").slice(0, 64)}
                    >
                        <Select
                            style={{ width: "100%" }}
                            allowClear
                            showSearch
                        >
                            {REFERENCE_TYPES.map((type) => (
                                <Option key={type} value={type}>{type}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={8} style={{
                background: "#f1f1f182",
                borderRadius: 3,
                padding: 5
            }}>
                <Col span={24}>
                    <Form.Item label={'Search location: ' + layer.formatted_address} >
                        {isLoaded ? (
                            <Autocomplete ref={autoMap} onPlaceChanged={OnchangePlacesAutocomplete}>
                                <input
                                    type="text"
                                    placeholder="Search a place to fill Latitude/Longitude..."
                                    className={"input_search_google_map"}
                                    style={{ width: "100%" }}
                                />
                            </Autocomplete>
                        ) : (
                            <input
                                type="text"
                                placeholder="Loading Google Maps..."
                                className={"input_search_google_map"}
                                style={{ width: "100%" }}
                                disabled
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Latitude"
                        name={["configsMoonPhase", "location", "latitude"]}
                    >
                        <InputNumber style={{ width: "100%" }} min={-90} max={90} step={0.0001} />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Longitude"
                        name={["configsMoonPhase", "location", "longitude"]}
                    >
                        <InputNumber style={{ width: "100%" }} min={-180} max={180} step={0.0001} />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item
                        name={["isDisplaySearchLocation"]}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Checkbox>Display location search</Checkbox>
                    </Form.Item>
                </Col>
            </Row>

            
            <Row gutter={8}>
                <Col span={12} style={{
                    display: 'none'
                }}>
                    <Form.Item
                        label="Style"
                        name={["configsMoonPhase", "render", "style"]}
                        initialValue={"photoreal"}
                    >
                        <Select style={{ width: "100%" }}>
                            <Option value="solid">Solid</Option>
                            <Option value="photoreal">Photoreal</Option>
                            <Option value="realistic">Realistic</Option>
                            <Option value="minimal">Minimal</Option>
                        </Select>
                    </Form.Item>
                </Col>                
            </Row>
            
            <Row gutter={8} style={{
                display: 'none'
            }}>
                <Col span={12}>
                    <Form.Item
                        label="Width"
                        name={["configsMoonPhase", "render", "width"]}
                        initialValue={2000}
                    >
                        <InputNumber style={{ width: "100%" }} min={256} max={4000} step={1} />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        label="Height"
                        name={["configsMoonPhase", "render", "height"]}
                        initialValue={2000}
                        extra="Total pixels (width x height) max 16,000,000."
                    >
                        <InputNumber style={{ width: "100%" }} min={256} max={4000} step={1} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={8} style={{
                display: 'none'
            }}>
                <Col span={12}>
                    <Form.Item
                        label="Transparent background"
                        name={["configsMoonPhase", "render", "transparent"]}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Checkbox />
                    </Form.Item>
                </Col>
                
            </Row>
            
            <Row gutter={8} style={{
                display: 'none'
            }}>
                <Col span={12} style={{
                    display: 'none'
                }}>
                    <Form.Item
                        label="Orientation"
                        name={["configsMoonPhase", "render", "orientation"]}
                        initialValue={"northern"}
                    >
                        <Select style={{ width: "100%" }}>
                            <Option value="northern">Northern</Option>
                            <Option value="southern">Southern</Option>
                            <Option value="auto">Auto (theo Latitude)</Option>
                        </Select>
                    </Form.Item>
                </Col>  
            </Row>

            <Button
                style={{
                    height: "35px",
                    width: "100%",
                    fontSize: "16px",
                    fontWeight: "600",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
                loading={loading}
                type="primary"
                onClick={handleRecreate}
                icon={
                    <FaMagic
                        style={{
                            marginRight: 3,
                        }}
                    />
                }
            >
                Recreate
            </Button>
        </div>
    </> : null;
}


export default connect((state) => ({
  workspace: state.artwork.workspace,
}))(MoonPhaseSetting);
